import React, { useEffect } from "react";

import {
    DataGridPro,
    gridClasses,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarDensitySelector,
    GridToolbarExport,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
    useGridApiRef,
} from "@mui/x-data-grid-pro";
import {
    styled,
    alpha,
    Radio,
    FormControl,
    FormControlLabel,
    FormLabel,
    RadioGroup,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tabs,
    Tab,
    Box,
    CircularProgress,
} from "@mui/material";
import {
    hitter_pitch_columns,
    hitter_pitch_columns_mobile,
    pitcher_pitch_columns,
    pitcher_pitch_columns_mobile,
} from "./utils/ColumnDefs";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`pitch-tabpanel-${index}`}
            aria-labelledby={`pitch-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return { id: `pitch-tab-${index}`, "aria-controls": `pitch-tabpanel-${index}` };
}

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: theme.palette.grey[200],
        "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
            "@media (hover: none)": { backgroundColor: "transparent" },
        },
        "&.Mui-selected": {
            backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY + theme.palette.action.selectedOpacity
            ),
            "&:hover": {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity
                ),
                "@media (hover: none)": {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        ODD_OPACITY + theme.palette.action.selectedOpacity
                    ),
                },
            },
        },
    },
    [`& .${gridClasses.row}.odd`]: {
        backgroundColor: "white",
        "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
            "@media (hover: none)": { backgroundColor: "transparent" },
        },
        "&.Mui-selected": {
            backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY + theme.palette.action.selectedOpacity
            ),
            "&:hover": {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity
                ),
                "@media (hover: none)": {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        ODD_OPACITY + theme.palette.action.selectedOpacity
                    ),
                },
            },
        },
    },
}));

const PITCH_TYPES = [
    { code: "All", name: "All" },
    { code: "FF", name: "4-Seam" },
    { code: "SI", name: "Sinker" },
    { code: "FC", name: "Cutter" },
    { code: "SL", name: "Slider" },
    { code: "ST", name: "Sweeper" },
    { code: "CU", name: "Curve" },
    { code: "KC", name: "K-Curve" },
    { code: "CH", name: "Change" },
    { code: "FS", name: "Splitter" },
    { code: "SV", name: "Slurve" },
    { code: "KN", name: "Knuckle" },
];

export default function HitterPitchBoard(props: any) {
    const apiRef = useGridApiRef();
    const pitcherApiRef = useGridApiRef();

    const [tabValue, setTabValue] = React.useState(0);
    const [level, setLevel] = React.useState("AAA");
    const [season, setSeason] = React.useState("2026");
    const [pitchData, setPitchData] = React.useState<Array<any>>([]);
    const [pitcherData, setPitcherData] = React.useState<Array<any>>([]);
    const [hitterLoading, setHitterLoading] = React.useState(false);
    const [pitcherLoading, setPitcherLoading] = React.useState(false);
    const [organization, setOrganization] = React.useState("All");
    const [pitchType, setPitchType] = React.useState("All");
    const [minPitches, setMinPitches] = React.useState("25");

    const hitterColumns = props.isDesktop ? hitter_pitch_columns : hitter_pitch_columns_mobile;
    const pitcherColumns = props.isDesktop ? pitcher_pitch_columns : pitcher_pitch_columns_mobile;

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleLevel = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLevel(event.target.value);
    };

    const handleSeason = (event: SelectChangeEvent) => {
        setSeason(event.target.value as string);
    };

    const handleOrganization = (event: SelectChangeEvent) => {
        setOrganization(event.target.value as string);
    };

    const handlePitchType = (event: SelectChangeEvent) => {
        setPitchType(event.target.value as string);
    };

    const handleMinPitches = (event: SelectChangeEvent) => {
        setMinPitches(event.target.value as string);
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ backgroundColor: "#FFFFFF" }}>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarDensitySelector />
                <GridToolbarExport />
                <GridToolbarQuickFilter
                    quickFilterParser={(searchInput) =>
                        searchInput.split(",").map((value) => value.trim())
                    }
                    sx={{ marginLeft: "auto", width: props.isDesktop ? 500 : 250 }}
                />
            </GridToolbarContainer>
        );
    }

    useEffect(() => {
        const base = props.prod
            ? "https://oriolebird.pythonanywhere.com"
            : "";
        setHitterLoading(true);
        fetch(
            `${base}/hitter-pitch-leaderboard/${level}/${season}/?min_pitches=${minPitches}`,
            { headers: { Authorization: `Bearer ${localStorage.getItem("ps_token") ?? ""}` } }
        )
            .then((res) => res.json())
            .then((data) => {
                setPitchData(data.data);
                setHitterLoading(false);
            })
            .catch(() => setHitterLoading(false));
    }, [level, season, minPitches, props.prod]);

    useEffect(() => {
        const base = props.prod
            ? "https://oriolebird.pythonanywhere.com"
            : "";
        setPitcherLoading(true);
        fetch(
            `${base}/pitcher-pitch-leaderboard/${level}/${season}/?min_pitches=${minPitches}`,
            { headers: { Authorization: `Bearer ${localStorage.getItem("ps_token") ?? ""}` } }
        )
            .then((res) => res.json())
            .then((data) => {
                setPitcherData(data.data);
                setPitcherLoading(false);
            })
            .catch(() => setPitcherLoading(false));
    }, [level, season, minPitches, props.prod]);

    const filteredHitterRows = pitchData.filter((row: any) => {
        const orgMatch = organization === "All" || row.MLB_AbbName === organization;
        const pitchMatch = pitchType === "All" || row.pitch_type === pitchType;
        return orgMatch && pitchMatch;
    });

    const filteredPitcherRows = pitcherData.filter((row: any) => {
        const orgMatch = organization === "All" || row.MLB_AbbName === organization;
        const pitchMatch = pitchType === "All" || row.pitch_type === pitchType;
        return orgMatch && pitchMatch;
    });

    const filters = (
        <div
            style={{
                marginTop: "10px",
                marginBottom: "-20px",
                marginLeft: "30px",
                verticalAlign: "baseline",
            }}
        >
            <FormControl>
                <FormLabel id="level-radio-label">Level</FormLabel>
                <RadioGroup row aria-labelledby="level-radio-label" name="level-radio">
                    <FormControlLabel
                        value="A"
                        control={
                            <Radio
                                checked={level === "A"}
                                onChange={handleLevel}
                                value="A"
                                name="radio-buttons"
                                inputProps={{ "aria-label": "A" }}
                            />
                        }
                        label="A"
                    />
                    <FormControlLabel
                        value="AAA"
                        control={
                            <Radio
                                checked={level === "AAA"}
                                onChange={handleLevel}
                                value="AAA"
                                name="radio-buttons"
                                inputProps={{ "aria-label": "AAA" }}
                            />
                        }
                        label="AAA"
                    />
                </RadioGroup>
            </FormControl>

            <FormControl>
                <InputLabel id="season-label">Season</InputLabel>
                <Select
                    value={season}
                    label="Season"
                    onChange={handleSeason}
                    defaultValue="2026"
                >
                    <MenuItem value={2026}>2026</MenuItem>
                    <MenuItem value={2025}>2025</MenuItem>
                    <MenuItem value={2024}>2024</MenuItem>
                    <MenuItem value={2023}>2023</MenuItem>
                </Select>
            </FormControl>

            <FormControl style={{ marginLeft: "10px" }}>
                <InputLabel id="org-label">Organization</InputLabel>
                <Select
                    value={organization}
                    label="Organization"
                    onChange={handleOrganization}
                    defaultValue="All"
                    sx={{ width: "100px" }}
                >
                    <MenuItem value={"All"}>All</MenuItem>
                    <MenuItem value={"ARI"}>ARI</MenuItem>
                    <MenuItem value={"ATL"}>ATL</MenuItem>
                    <MenuItem value={"ATH"}>ATH</MenuItem>
                    <MenuItem value={"BAL"}>BAL</MenuItem>
                    <MenuItem value={"BOS"}>BOS</MenuItem>
                    <MenuItem value={"CHC"}>CHC</MenuItem>
                    <MenuItem value={"CHW"}>CHW</MenuItem>
                    <MenuItem value={"CIN"}>CIN</MenuItem>
                    <MenuItem value={"CLE"}>CLE</MenuItem>
                    <MenuItem value={"COL"}>COL</MenuItem>
                    <MenuItem value={"DET"}>DET</MenuItem>
                    <MenuItem value={"HOU"}>HOU</MenuItem>
                    <MenuItem value={"KCR"}>KCR</MenuItem>
                    <MenuItem value={"LAA"}>LAA</MenuItem>
                    <MenuItem value={"LAD"}>LAD</MenuItem>
                    <MenuItem value={"MIA"}>MIA</MenuItem>
                    <MenuItem value={"MIL"}>MIL</MenuItem>
                    <MenuItem value={"MIN"}>MIN</MenuItem>
                    <MenuItem value={"NYM"}>NYM</MenuItem>
                    <MenuItem value={"NYY"}>NYY</MenuItem>
                    <MenuItem value={"PHI"}>PHI</MenuItem>
                    <MenuItem value={"PIT"}>PIT</MenuItem>
                    <MenuItem value={"SDP"}>SDP</MenuItem>
                    <MenuItem value={"SEA"}>SEA</MenuItem>
                    <MenuItem value={"SFG"}>SFG</MenuItem>
                    <MenuItem value={"STL"}>STL</MenuItem>
                    <MenuItem value={"TBR"}>TBR</MenuItem>
                    <MenuItem value={"TEX"}>TEX</MenuItem>
                    <MenuItem value={"TOR"}>TOR</MenuItem>
                    <MenuItem value={"WSN"}>WSN</MenuItem>
                </Select>
            </FormControl>

            <FormControl style={{ marginLeft: "10px" }}>
                <InputLabel id="pitch-type-label">Pitch Type</InputLabel>
                <Select
                    value={pitchType}
                    label="Pitch Type"
                    onChange={handlePitchType}
                    defaultValue="All"
                    sx={{ width: "120px" }}
                >
                    {PITCH_TYPES.map((pt) => (
                        <MenuItem key={pt.code} value={pt.code}>
                            {pt.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl style={{ marginLeft: "10px" }}>
                <InputLabel id="min-pitches-label">Min Pitches</InputLabel>
                <Select
                    value={minPitches}
                    label="Min Pitches"
                    onChange={handleMinPitches}
                    defaultValue="25"
                    sx={{ width: "105px" }}
                >
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                    <MenuItem value={150}>150</MenuItem>
                    <MenuItem value={200}>200</MenuItem>
                    <MenuItem value={250}>250</MenuItem>
                    <MenuItem value={300}>300</MenuItem>
                    <MenuItem value={400}>400</MenuItem>
                    <MenuItem value={500}>500</MenuItem>
                </Select>
            </FormControl>
        </div>
    );

    return (
        <div style={{ width: "100%" }}>
            <title>Prospect Savant – Pitch Type Leaderboard</title>
            <meta name="description" content="Minor league pitcher and hitter performance by pitch type" />

            <Box sx={{ borderBottom: 1, borderColor: "divider", marginLeft: "30px" }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="pitch type tabs">
                    <Tab label="Hitters" {...a11yProps(0)} />
                    <Tab label="Pitchers" {...a11yProps(1)} />
                </Tabs>
            </Box>

            <CustomTabPanel value={tabValue} index={0}>
                {filters}
                {hitterLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" mt={6}>
                        <CircularProgress />
                    </Box>
                ) : (
                <StripedDataGrid
                    apiRef={apiRef}
                    sx={{
                        height: "80vh",
                        width: props.isDesktop ? "99%" : "100%",
                        marginTop: "20px",
                        marginLeft: "auto",
                        marginRight: "auto",
                    }}
                    rows={filteredHitterRows}
                    columns={hitterColumns}
                    getRowId={(row) => `${row.player_id}-${row.pitch_type}`}
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                    }
                    disableColumnResize={false}
                    slots={{ toolbar: CustomToolbar }}
                    initialState={{
                        density: "compact",
                        pinnedColumns: { left: ["name"] },
                    }}
                />
                )}
            </CustomTabPanel>

            <CustomTabPanel value={tabValue} index={1}>
                {filters}
                {pitcherLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" mt={6}>
                        <CircularProgress />
                    </Box>
                ) : (
                <StripedDataGrid
                    apiRef={pitcherApiRef}
                    sx={{
                        height: "80vh",
                        width: props.isDesktop ? "99%" : "100%",
                        marginTop: "20px",
                        marginLeft: "auto",
                        marginRight: "auto",
                    }}
                    rows={filteredPitcherRows}
                    columns={pitcherColumns}
                    getRowId={(row) => `${row.player_id}-${row.pitch_type}`}
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                    }
                    disableColumnResize={false}
                    slots={{ toolbar: CustomToolbar }}
                    initialState={{
                        density: "compact",
                        pinnedColumns: { left: ["name"] },
                    }}
                />
                )}
            </CustomTabPanel>
        </div>
    );
}

